import { z } from 'zod';
import { idSchema } from './common.js';
/**
 * A scene's picture: one from the drawn catalog, or one an admin generated and approved
 * — VIS-17. Not in the package barrel, like `scene-tools`: the player reads the URL
 * helpers on the mobile boot path and Metro does not tree-shake.
 */
/** Where the catalog build publishes each image — `CATALOG_HOST` in `scripts/origins.mjs`. */
const CATALOG_ORIGIN = 'https://catalog.startdecide.com/art/';
const EXTENSION = '.svg';
/**
 * Every image the catalog draws, and its shape. The drawings are in
 * `packages/ui/src/v2/product/scene-art`, whose registry must name exactly these; they
 * are listed here so the API and the admin, which cannot import that package, know
 * which exist.
 */
export const sceneArtShapes = {
    profitCashHook: 'wide',
    firstOfferHook: 'wide',
    bottleneckArticle: 'wide',
    feedbackHook: 'wide',
    channelArticle: 'wide',
    sayingNoRecap: 'wide',
    talkShapeHook: 'wide',
    discoveryArticle: 'wide',
    experimentHook: 'wide',
    leadDecisionRecap: 'wide',
    baseRateArticle: 'wide',
    shelfRecap: 'wide',
    runwayRecap: 'wide',
    rivalCutHook: 'wide',
    threeStatementsHook: 'wide',
    runwayHook: 'wide',
    bottleneckHook: 'wide',
    baseRateHook: 'wide',
    whoIsItForHook: 'wide',
    whatNumberMovedHook: 'wide',
    whereToPlayHook: 'wide',
    sayingNoHook: 'wide',
    walkAwayHook: 'wide',
    tradingHook: 'wide',
    priceObjectionHook: 'wide',
    discoveryHook: 'wide',
    firstWeekHook: 'wide',
    notGoodEnoughHook: 'wide',
    askLastTimeHook: 'wide',
    orderOfSpendHook: 'wide',
    leadDecisionHook: 'wide',
    oneNumberHook: 'wide',
    sayItOutLoudHook: 'wide',
    strategyCourse: 'square',
    financeCourse: 'square',
    negotiationCourse: 'square',
    operationsCourse: 'square',
    cashRunwayModule: 'square',
    talkStructureModule: 'square',
    decisionsCourse: 'square',
    readingNumbersModule: 'square',
    bottlenecksModule: 'square',
    judgementModule: 'square',
    marketingCourse: 'square',
    positioningModule: 'square',
    channelsBudgetModule: 'square',
    whereToPlayModule: 'square',
    strategyPressureModule: 'square',
    salesCourse: 'square',
    preparationModule: 'square',
    atTheTableModule: 'square',
    discoveryModule: 'square',
    objectionsModule: 'square',
    leadershipCourse: 'square',
    first90DaysModule: 'square',
    difficultConversationsModule: 'square',
    entrepreneurshipCourse: 'square',
    testingIdeaModule: 'square',
    firstMoneyModule: 'square',
    speakingCourse: 'square',
    communicationCourse: 'square',
    deliveryModule: 'square',
    writtenClarityModule: 'square',
};
function isSceneArtName(name) {
    return Object.hasOwn(sceneArtShapes, name);
}
export function sceneArtUrl(name) {
    return `${CATALOG_ORIGIN}${name}${EXTENSION}`;
}
/**
 * The catalog image a scene's or a collection's `imageUrl` names, or `undefined` for
 * any URL the catalog does not publish.
 */
export function sceneArtName(url) {
    if (url?.startsWith(CATALOG_ORIGIN) !== true || !url.endsWith(EXTENSION)) {
        return undefined;
    }
    const name = url.slice(CATALOG_ORIGIN.length, -EXTENSION.length);
    return isSceneArtName(name) ? name : undefined;
}
/** The catalog images sized for a scene, which is every one an admin can attach. */
export const sceneSizedArt = Object.keys(sceneArtShapes).filter((name) => sceneArtShapes[name] === 'wide');
export const sceneImageSchemeSchema = z
    .enum(['light', 'dark'])
    .describe('Which colour scheme to draw the image in.');
export const sceneImageFileQuerySchema = z
    .object({
    scheme: sceneImageSchemeSchema
        .optional()
        .describe('Absent: one file holding both, shown by the viewer’s own scheme.'),
})
    .describe('GET /scene-images/:file');
const PICTURE = /\/pictures\/[^/]+$/;
const SCENE_IMAGE = /\/scene-images\/([a-z0-9]+)\.svg$/;
export const sceneImagePath = (id) => `/scene-images/${id}.svg`;
/** A drawn lesson picture — GEN-231. Raster, so one file serves both schemes. */
export const picturePath = (id) => `/pictures/${id}`;
/** Whether an `imageUrl` names a drawn picture rather than vector art. */
export const isPictureUrl = (url) => url !== undefined && PICTURE.test(url);
/** A lesson's cover, at a path its slug names, because `Lesson` has no image column. */
export const coverPath = (slug) => `/pictures/covers/${slug}`;
/** The generated image an `imageUrl` names, or `undefined` for any other URL. */
export function sceneImageId(url) {
    return url === undefined ? undefined : SCENE_IMAGE.exec(url)?.[1];
}
/** The same image drawn in one scheme, which is how the app asks for it. */
export const sceneImageUrlIn = (url, scheme) => `${url}?scheme=${scheme}`;
export const sceneImageVersionSchema = z
    .object({
    id: idSchema,
    prompt: z.string().describe('What the admin asked for.'),
    feedback: z
        .string()
        .nullable()
        .describe('The refinement asked of `basedOnId`, when this is one.'),
    basedOnId: idSchema
        .nullable()
        .describe('The version this one refined or regenerated.'),
    light: z.string().describe('The drawing in the light scheme, as SVG.'),
    dark: z.string().describe('The drawing in the dark scheme, as SVG.'),
    refusal: z
        .string()
        .nullable()
        .describe('Why this version cannot be approved, or null when it can.'),
    approved: z.boolean().describe('Whether an admin has approved it.'),
    createdAt: z.iso.datetime(),
})
    .describe('One generated version of a scene image.');
export const sceneImagesSchema = z
    .object({
    suggestedPrompt: z
        .string()
        .describe('A first prompt, written from the scene’s own text.'),
    versions: z
        .array(sceneImageVersionSchema)
        .describe('Every version generated for the scene, newest first.'),
})
    .describe('GET /authoring/scenes/:id/images');
export const generateSceneImageSchema = z
    .strictObject({
    prompt: z.string().trim().min(1).max(2000),
    feedback: z
        .string()
        .trim()
        .min(1)
        .max(500)
        .optional()
        .describe('A change to ask of `basedOnId` — "warmer", "no chart".'),
    basedOnId: idSchema
        .optional()
        .describe('The version to refine. Required with `feedback`.'),
})
    .describe('POST /authoring/scenes/:id/images');
export const approvedSceneImageSchema = z
    .object({
    imageUrl: z.url().describe('What the scene now shows.'),
})
    .describe('POST /authoring/scene-images/:id/approve');
export const chooseSceneImageSchema = z
    .strictObject({
    imageUrl: z
        .url()
        .nullable()
        .describe('A scene-sized catalog image, or null to take the image off.'),
})
    .describe('PUT /authoring/scenes/:id/image');
//# sourceMappingURL=scene-image.js.map